import type { EventData } from "react-joyride";

/**
 * TES-1659: react-joyride v3's built-in scroll engine scrolls the *window*. In
 * CheckMaty the window cannot scroll — AppLayout's root is
 * `flex h-screen flex-col overflow-hidden` and the REAL scroll container is the
 * inner `<main className="flex-1 overflow-auto …">`. Dashboard tour targets live
 * inside that `<main>`, so joyride never brings steps below the fold into view
 * and the tooltip renders off-screen (proven: step 2 tooltip at y=1059 in an
 * 800px viewport).
 *
 * Fix: on every step change we manually scroll the target's real scrollable
 * ancestor(s) so the target is centred in the viewport. `scrollIntoView` with
 * `block: "center"` walks *all* scrollable ancestors (including `<main>` and the
 * window), which is exactly what joyride's window-only engine misses. We then
 * nudge for the step's `scrollOffset` so the sticky header doesn't cover the
 * spotlight. Deliberately small + dependency-free so both tours can share it.
 */

/** Joyride event types at which we (re)scroll the target into view. */
const SCROLL_EVENTS = new Set<EventData["type"]>(["step:before", "tooltip"]);

/** Resolve a joyride `step.target` (selector | element) to a DOM element. */
function resolveTarget(target: unknown): HTMLElement | null {
  if (target instanceof HTMLElement) {
    return target;
  }
  if (typeof target === "string") {
    return document.querySelector<HTMLElement>(target);
  }
  return null;
}

/**
 * Bring `target` into the viewport of its real scroll container so joyride can
 * position the tooltip on-screen. Safe to call when the target is absent (no-op).
 *
 * Strategy (handles BOTH short and viewport-taller targets, e.g. the dashboard
 * "Teamübersicht" section on a 390x844 mobile screen):
 *  - If the target FITS in the container viewport → centre it (`block:"center"`)
 *    so there is room on every side for whatever placement react-floater picks.
 *  - If the target is TALLER than the viewport → centring would push BOTH its top
 *    and bottom off-screen and a top-placed tooltip lands off the top (the mobile
 *    step-6 clip). Instead align the target's TOP with `scrollOffset` headroom,
 *    which leaves the rest of the viewport below it; react-floater then flips the
 *    tooltip below the anchor and it fits.
 * `scrollOffset` is the per-step top headroom (clears the sticky header).
 */
function scrollTargetIntoView(
  target: unknown,
  scrollOffset = 0,
  scroller: HTMLElement | null = resolveTarget(target),
): void {
  if (!scroller) {
    return;
  }

  const container = findScrollableAncestor(scroller);
  const viewportHeight = container?.clientHeight ?? window.innerHeight;
  const targetTallerThanViewport = scroller.getBoundingClientRect().height > viewportHeight;

  scroller.scrollIntoView({
    block: targetTallerThanViewport ? "start" : "center",
    inline: "nearest",
    behavior: "auto",
  });

  if (scrollOffset <= 0 || !container) {
    return;
  }

  // Nudge the scroll container up by `scrollOffset` so the sticky header (and, for
  // top-placed tooltips on short targets, the tooltip itself) clear the top edge.
  container.scrollTop = Math.max(0, container.scrollTop - scrollOffset);
}

/** Walk up from `el` to the nearest vertically-scrollable ancestor (or null). */
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const style = getComputedStyle(node);
    const overflowY = style.overflowY;
    const scrollable =
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      node.scrollHeight > node.clientHeight;
    if (scrollable) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Joyride `onEvent` adapter: on step-entry events, scroll the step's target into
 * view honouring its `scrollOffset`. Returns true when a scroll was attempted so
 * callers/tests can assert it ran.
 */
export function handleTourScroll(data: Pick<EventData, "type" | "step">): boolean {
  if (!SCROLL_EVENTS.has(data.type)) {
    return false;
  }
  const step = data.step;
  if (!step) {
    return false;
  }
  scrollTargetIntoView(step.target, step.scrollOffset ?? 0);
  return true;
}
